import type {
  CanvasObject,
  TableCanvasObject,
  TableCellInlineStyles,
  TableCellInlineStylesRow,
} from "./canvas-object";
import {
  estimateEmWidth,
  resolveMarkdownTableColumnWidths,
  TABLE_CELL_PADDING_EM,
  TABLE_MIN_COLUMN_EM,
  TABLE_ROW_LINE_EM,
  TABLE_ROW_MIN_HEIGHT_EM,
} from "./markdown-source-map";
import { jsonStringByteLength } from "./text-body-limit";
import {
  applyTextInlineRangePatches,
  isSameTextInlineStyle,
  isValidTextInlineStyleRanges,
  normalizeTextInlineStyles,
  rebaseTextInlineStyles,
  TEXT_INLINE_STYLE_RUNS_MAX,
  type TextInlineRangePatch,
  type TextInlineStyleRange,
} from "./text-inline-style";

/**
 * 表の構造 revision（行・列の増減のたびに 1 つ進む番号）の上限。
 * セル更新の op は「どの形の表に対して組み立てられたか」をこの番号で表すため、
 * 採番を進められなくなった時点で保存を拒否し、番号の一致による排他が壊れないようにする。
 */
export const TABLE_STRUCTURE_REVISION_MAX = Number.MAX_SAFE_INTEGER;

/** 構造 revision を進められなくなった表を拒否するときの、利用者向けの理由。 */
export const TABLE_STRUCTURE_REVISION_EXHAUSTED_REASON =
  "この表は更新回数の上限に達しました。表を作り直してから編集してください。";

/** 表図形が保持できる最大行数（ヘッダ行を含む）。 */
export const TABLE_MAX_ROWS = 50;
/** 表図形が保持できる最大列数。 */
export const TABLE_MAX_COLUMNS = 20;
/** 1 セルに保持できる最大文字数。 */
export const TABLE_CELL_MAX_LENGTH = 512;
/** 表全体に保持できるセル本文の最大文字数。 */
export const TABLE_CELLS_MAX_TOTAL_CHARS = 8192;
/** 表全体の cells 配列を JSON 化したときの最大バイト数。 */
export const TABLE_CELLS_MAX_JSON_BYTES = 24_576;
/** 列幅比率 1 要素の上限。 */
export const TABLE_COLUMN_FRACTION_MAX = 1_000_000;

/** ギザギザなセル配列を、最長行へ空文字列を補って長方形にそろえる。 */
export function normalizeTableCells(
  cells: readonly (readonly string[])[]
): readonly (readonly string[])[] {
  if (cells.length === 0) return [[""]];
  const columnCount = Math.max(1, ...cells.map((row) => row.length));
  return cells.map((row) =>
    row.length === columnCount
      ? row
      : [...row, ...new Array<string>(columnCount - row.length).fill("")]
  );
}

/** cells 配列を JSON 化したときの UTF-8 バイト数を、文字列エスケープ込みで数える。 */
export function tableCellsJsonByteLength(cells: readonly (readonly string[])[]): number {
  let total = 2; // 外側の []
  for (const [rowIndex, row] of cells.entries()) {
    if (rowIndex > 0) total += 1; // 行間のカンマ
    total += 2; // 行の []
    for (const [columnIndex, cell] of row.entries()) {
      if (columnIndex > 0) total += 1; // セル間のカンマ
      total += 2 + jsonStringByteLength(cell); // 文字列の引用符 + 本文
    }
  }
  return total;
}

/** サーバとクリップボードが共有する、表セル配列の不変条件を検証する。 */
export function isValidTableCells(value: unknown): value is readonly (readonly string[])[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > TABLE_MAX_ROWS) return false;
  const first = value[0];
  if (!Array.isArray(first) || first.length < 1 || first.length > TABLE_MAX_COLUMNS) return false;
  const columnCount = first.length;
  let totalChars = 0;
  for (const row of value) {
    if (!Array.isArray(row) || row.length !== columnCount) return false;
    for (const cell of row) {
      if (typeof cell !== "string" || cell.length > TABLE_CELL_MAX_LENGTH) return false;
      totalChars += cell.length;
      if (totalChars > TABLE_CELLS_MAX_TOTAL_CHARS) return false;
    }
  }
  return (
    tableCellsJsonByteLength(value as readonly (readonly string[])[]) <= TABLE_CELLS_MAX_JSON_BYTES
  );
}

//------------------------------------------------------------------------------
// セルの部分書式（cellInlineStyles）
//------------------------------------------------------------------------------

/**
 * セルの部分書式を正規化するときの基底書式。
 *
 * テキスト図形は図形全体の書式を基底として渡し、基底と同値の override を落とすが、表では
 * 渡さない（空の基底を使う）。表のヘッダ行は `object.color` ではなくヘッダ専用の文字色と
 * 太字で描かれるため、`object.color` を基底にすると「ヘッダ行では見えているのに保存時に
 * 消える run」が生まれるからである。保存サイズは正規化の他の規則（空 style の除去・隣接
 * 同値の結合）で抑える。
 */
const TABLE_CELL_INLINE_STYLE_BASE = {} as const;

/** セル 1 つぶんの run 配列を、テキスト図形と同じ規則で正規形へそろえる。 */
function normalizeCellRuns(
  runs: readonly TextInlineStyleRange[] | null | undefined,
  text: string
): readonly TextInlineStyleRange[] | undefined {
  if (runs === undefined || runs === null || runs.length === 0) return undefined;
  const normalized = normalizeTextInlineStyles({
    base: TABLE_CELL_INLINE_STYLE_BASE,
    runs,
    text,
  });
  return normalized.length === 0 ? undefined : normalized;
}

/**
 * セル 1 つの部分書式へ範囲パッチを当て、正規形の run 配列を返す。
 *
 * 基底書式には、セルの正規化・追従（{@link normalizeTableCellInlineStyles} と内部の rebase）と
 * **同じ空の基底**（{@link TABLE_CELL_INLINE_STYLE_BASE}）を使う。基底が食い違うと、パッチ適用側だけが
 * 「基底と同値」と判断した override を落とし、保存直後に書式が消えたように見える。表のセルへ
 * 範囲書式を当てる経路は、この関数だけを通す（呼び出し側で基底を書き起こさない）。
 */
export function applyTableCellInlineRangePatches(params: {
  readonly runs: readonly TextInlineStyleRange[] | undefined;
  readonly text: string;
  readonly patches: readonly TextInlineRangePatch[];
}): readonly TextInlineStyleRange[] {
  return applyTextInlineRangePatches({
    base: TABLE_CELL_INLINE_STYLE_BASE,
    patches: params.patches,
    runs: params.runs,
    text: params.text,
  });
}

/**
 * セル 1 つぶんの run 配列を、本文の差し替えへ追従させる（テキスト図形の本文更新と同じ共有関数）。
 * `inlineStyles` を載せない旧クライアント由来のセル更新で、既存の部分書式を失わないために使う。
 */
function rebaseCellRuns(
  runs: readonly TextInlineStyleRange[] | undefined,
  prevText: string,
  nextText: string
): readonly TextInlineStyleRange[] | undefined {
  if (runs === undefined || runs.length === 0) return undefined;
  const rebased = rebaseTextInlineStyles({
    base: TABLE_CELL_INLINE_STYLE_BASE,
    nextText,
    prevText,
    runs,
  });
  return rebased.length === 0 ? undefined : rebased;
}

/**
 * セルごとの部分書式を `cells` と同じ形へそろえる。
 *
 * - 行数・列数が合わない場合は、`cells` の形へ切り詰め、足りない分は `null` で埋める。
 * - 各セルの run はテキスト図形と同じ正規化（重なりの解消・昇順・空区間の除去・本文長での
 *   切り詰め）を通す。表専用の正規化は書き起こさない。
 * - 空になった run 配列は `null` へ畳み、全セルが空なら配列ごと `undefined` を返す
 *   （保存サイズを増やさず、部分書式を持たない表と同じ保存内容にする）。
 *
 * **欠落は `undefined` ではなく `null` で表す。** 配列の中の `undefined` は、DynamoDB へ保存する
 * ときの `marshall({ removeUndefinedValues: true })` で**要素ごと取り除かれて添字がずれる**
 * （`[[runs], undefined], undefined]` が `[[runs]]` になる）ため、読み直すと別のセルの書式に
 * なってしまう。保存スキーマ（`canvas-object-schema.ts` の `TableCellInlineStylesSchema`）も
 * 欠落を `null` で受けており、JSON 往復後の形も `null` である。`null` へそろえることで、
 * 正規化の出力・保存・読み直しの 3 つが同じ形になる。
 */
export function normalizeTableCellInlineStyles(params: {
  readonly cells: readonly (readonly string[])[];
  readonly cellInlineStyles: TableCellInlineStyles | undefined;
}): TableCellInlineStyles | undefined {
  const source = params.cellInlineStyles;
  if (source === undefined) return undefined;
  let hasAny = false;
  const rows = params.cells.map((cells, rowIndex) => {
    const sourceRow = source[rowIndex];
    if (sourceRow === undefined || sourceRow === null) return null;
    let rowHasAny = false;
    const columns = cells.map((cell, columnIndex) => {
      const runs = normalizeCellRuns(sourceRow[columnIndex], cell);
      if (runs !== undefined) rowHasAny = true;
      return runs ?? null;
    });
    if (!rowHasAny) return null;
    hasAny = true;
    return columns;
  });
  return hasAny ? rows : undefined;
}

/**
 * 部分書式の配列からセル 1 つぶんを取り出す（3 重の入れ子を呼び出し側にたどらせないための入口）。
 * 表オブジェクトを持たない描画部品（配置プレビューと共有する表グリッドなど）はこちらを使う。
 */
export function cellInlineStylesOf(
  cellInlineStyles: TableCellInlineStyles | undefined,
  row: number,
  column: number
): readonly TextInlineStyleRange[] | undefined {
  // JSON 往復で欠落が `null` になるため、読み出し側では `undefined` へそろえる。
  return cellInlineStyles?.[row]?.[column] ?? undefined;
}

/** セル 1 つの部分書式を表オブジェクトから取り出す。 */
export function cellInlineStylesAt(
  table: TableCanvasObject,
  row: number,
  column: number
): readonly TextInlineStyleRange[] | undefined {
  return cellInlineStylesOf(table.cellInlineStyles, row, column);
}

/** 部分書式の配列を差し替えた表を返す。空（全セルなし）ならフィールドごと落とす。 */
function withTableCellInlineStyles(
  table: TableCanvasObject,
  cells: readonly (readonly string[])[],
  cellInlineStyles: TableCellInlineStyles | undefined
): TableCanvasObject {
  const normalized = normalizeTableCellInlineStyles({ cells, cellInlineStyles });
  // 表示側が毎回無視する死んだ値を残さないよう、部分書式が無くなったらキーごと落とす。
  const { cellInlineStyles: _previous, ...rest } = table;
  return {
    ...rest,
    cells,
    ...(normalized === undefined ? {} : { cellInlineStyles: normalized }),
  };
}

/** セル 1 つの部分書式だけを差し替えた表を返す（正規化つき）。範囲外は元の表をそのまま返す。 */
export function withCellInlineStyles(
  table: TableCanvasObject,
  row: number,
  column: number,
  runs: readonly TextInlineStyleRange[] | undefined
): TableCanvasObject {
  const cell = table.cells[row]?.[column];
  if (cell === undefined) return table;
  return withTableCellInlineStyles(
    table,
    table.cells,
    replaceCellInlineStyles(table.cellInlineStyles, table.cells, row, column, runs)
  );
}

/** セル 1 つの run だけを差し替えた部分書式配列を返す（形は `cells` へそろえる）。 */
function replaceCellInlineStyles(
  source: TableCellInlineStyles | undefined,
  cells: readonly (readonly string[])[],
  row: number,
  column: number,
  runs: readonly TextInlineStyleRange[] | undefined
): TableCellInlineStyles | undefined {
  // 部分書式を持たない表へ部分書式を付けない更新では、配列そのものを作らない。
  // セル更新は打鍵ごとに呼ばれるため、既存の表を従来どおり割り当てなしで通す。
  if (source === undefined && runs === undefined) return undefined;
  return cells.map((cellRow, rowIndex) => {
    const sourceRow = source?.[rowIndex];
    if (rowIndex !== row) return sourceRow;
    return cellRow.map((_cell, columnIndex) =>
      columnIndex === column ? runs : sourceRow?.[columnIndex]
    );
  });
}

/**
 * セルごとの部分書式を JSON 化したときの UTF-8 バイト数。
 * 配列要素の `undefined` は `JSON.stringify` と同じく `null`（4 バイト）として数える。
 */
export function tableCellInlineStylesJsonByteLength(
  cellInlineStyles: TableCellInlineStyles | undefined
): number {
  if (cellInlineStyles === undefined) return 0;
  let total = 2; // 外側の []
  for (const [rowIndex, row] of cellInlineStyles.entries()) {
    if (rowIndex > 0) total += 1; // 行間のカンマ
    if (row === undefined || row === null) {
      total += 4; // null
      continue;
    }
    total += 2; // 行の []
    for (const [columnIndex, runs] of row.entries()) {
      if (columnIndex > 0) total += 1; // セル間のカンマ
      if (runs === undefined || runs === null) {
        total += 4; // null
        continue;
      }
      total += 2; // run 配列の []
      for (const [runIndex, run] of runs.entries()) {
        if (runIndex > 0) total += 1; // run 間のカンマ
        total += inlineStyleRangeJsonByteLength(run);
      }
    }
  }
  return total;
}

/** run 1 件（`{"start":..,"end":..,"style":{..}}`）の JSON バイト数。 */
function inlineStyleRangeJsonByteLength(run: TextInlineStyleRange): number {
  // `{"start":` + 数値 + `,"end":` + 数値 + `,"style":` + style + `}`
  let total = 9 + String(run.start).length + 7 + String(run.end).length + 9 + 1;
  total += 2; // style の {}
  let keys = 0;
  if (run.style.color !== undefined) {
    keys += 1;
    total += 8 + 2 + jsonStringByteLength(run.style.color); // `"color":` + `"..."`
  }
  if (run.style.fill !== undefined) {
    keys += 1;
    total += 7 + 2 + jsonStringByteLength(run.style.fill); // `"fill":` + `"..."`
  }
  if (run.style.fontSize !== undefined) {
    keys += 1;
    total += 11 + String(run.style.fontSize).length; // `"fontSize":` + 数値
  }
  if (run.style.bold !== undefined) {
    keys += 1;
    total += 7 + (run.style.bold ? 4 : 5); // `"bold":` + true / false
  }
  return total + Math.max(0, keys - 1); // キー間のカンマ
}

/** 1 セルに設定できる部分書式の件数上限を超えたセルがあるか（テキスト図形と同じ上限を使う）。 */
function exceedsCellInlineStyleRunsLimit(cellInlineStyles: TableCellInlineStyles | undefined): boolean {
  if (cellInlineStyles === undefined) return false;
  return cellInlineStyles.some((row) =>
    (row ?? []).some((runs) => (runs?.length ?? 0) > TEXT_INLINE_STYLE_RUNS_MAX)
  );
}

/** 部分書式の件数上限を超えたときの、利用者向けの理由。 */
const TABLE_CELL_INLINE_STYLE_RUNS_LIMIT_REASON =
  `1 つのセルに設定できる部分書式の件数（${TEXT_INLINE_STYLE_RUNS_MAX}件）を超えています。` +
  `範囲をまとめてお試しください。`;

/**
 * 表の保存サイズ（セル本文 + セルの部分書式）が JSON バイト上限に収まるか。
 *
 * 部分書式を足すと保存サイズが増えるため、上限の数え方だけを「本文 + 部分書式」へ広げる
 * （上限値そのものは変えない）。部分書式を持たない表では `isValidTableCells` の判定と
 * 完全に同じ結果になる。
 */
export function isValidTableStorageSize(
  cells: readonly (readonly string[])[],
  cellInlineStyles: TableCellInlineStyles | undefined
): boolean {
  return (
    tableCellsJsonByteLength(cells) + tableCellInlineStylesJsonByteLength(cellInlineStyles) <=
    TABLE_CELLS_MAX_JSON_BYTES
  );
}

/**
 * 外部入力（API・MCP・REST）の `cellInlineStyles` が保存できる形かを検証する。
 * 形は `cells` と同じ行×列で、各セルの run はテキスト図形と同じ正規形であることを要求する。
 */
export function isValidTableCellInlineStyles(
  value: unknown,
  cells: readonly (readonly string[])[]
): value is TableCellInlineStyles {
  if (!Array.isArray(value)) return false;
  if (value.length !== cells.length) return false;
  for (const [rowIndex, row] of value.entries()) {
    if (row === undefined || row === null) continue;
    if (!Array.isArray(row)) return false;
    const cellRow = cells[rowIndex];
    if (!cellRow || row.length !== cellRow.length) return false;
    for (const [columnIndex, runs] of row.entries()) {
      if (runs === undefined || runs === null) continue;
      if (!Array.isArray(runs)) return false;
      // 空配列は「部分書式なし」を `null` で表す正規形に反する（往復で配列が変わる）。
      if (runs.length === 0) return false;
      if (!isValidTextInlineStyleRanges(runs, cellRow[columnIndex] ?? "")) return false;
    }
  }
  return true;
}

/** 保存済み列幅比率が列数と数値上限を満たすか検証する。 */
export function isValidTableColumnFractions(
  value: unknown,
  columnCount: number
): value is readonly number[] {
  if (!Array.isArray(value) || value.length !== columnCount) return false;
  let total = 0;
  for (const fraction of value) {
    if (
      typeof fraction !== "number" ||
      !Number.isFinite(fraction) ||
      fraction <= 0 ||
      fraction > TABLE_COLUMN_FRACTION_MAX
    ) {
      return false;
    }
    total += fraction;
  }
  return Number.isFinite(total) && total > 0;
}

/** 最小幅を守りながら目標比率へ使用可能幅を配分する。 */
function distributeWithMinimum(
  weights: readonly number[],
  availablePx: number,
  minimumPx: number
): readonly number[] {
  if (weights.length === 0) return [];
  if (availablePx <= minimumPx * weights.length) {
    return new Array<number>(weights.length).fill(minimumPx);
  }
  const remaining = new Set(weights.map((_weight, index) => index));
  const widths = new Array<number>(weights.length).fill(minimumPx);
  let pool = availablePx;
  // 未確定列に残っている重みの合計を持ち、water-filling の各周回で再配分する。
  let weightTotal = weights.reduce((sum, value) => sum + value, 0);
  while (remaining.size > 0) {
    let fixed = false;
    for (const index of remaining) {
      const candidate =
        weightTotal > 0 ? (pool * weights[index]!) / weightTotal : pool / remaining.size;
      if (candidate < minimumPx) {
        widths[index] = minimumPx;
        pool -= minimumPx;
        weightTotal -= weights[index]!;
        remaining.delete(index);
        fixed = true;
      }
    }
    if (!fixed) break;
  }
  for (const index of remaining) {
    widths[index] =
      weightTotal > 0 ? (pool * weights[index]!) / weightTotal : pool / remaining.size;
  }
  return widths;
}

/** 画面・PDF・サムネイルで共有する表図形の列幅（px）を返す。 */
export function resolveTableObjectColumnWidths(params: {
  readonly cells: readonly (readonly string[])[];
  readonly columnFractions?: readonly number[] | undefined;
  readonly availablePx: number;
  readonly fontPx: number;
}): readonly number[] {
  const columnCount = params.cells[0]?.length ?? 0;
  if (columnCount === 0) return [];
  const fontPx = Number.isFinite(params.fontPx) && params.fontPx > 0 ? params.fontPx : 16;
  const availablePx = Number.isFinite(params.availablePx) ? Math.max(0, params.availablePx) : 0;
  const minimumPx = TABLE_MIN_COLUMN_EM * fontPx;
  if (isValidTableColumnFractions(params.columnFractions, columnCount)) {
    return distributeWithMinimum(params.columnFractions, availablePx, minimumPx);
  }

  const naturalEmWidths = new Array<number>(columnCount).fill(0);
  for (const row of params.cells) {
    for (let column = 0; column < columnCount; column += 1) {
      naturalEmWidths[column] = Math.max(
        naturalEmWidths[column]!,
        estimateEmWidth(row[column] ?? "")
      );
    }
  }
  const availableEm = availablePx / fontPx;
  const base = resolveMarkdownTableColumnWidths({
    naturalEmWidths,
    availableEm,
    cellPaddingEm: TABLE_CELL_PADDING_EM,
    minColumnEm: TABLE_MIN_COLUMN_EM,
  });
  // 共有計算が返した自然幅の総和と枠の余剰を比較し、余った場合だけ均等に広げる。
  const baseTotal = base.reduce((sum, width) => sum + width, 0);
  // Markdown 表の自然幅が枠より小さい場合だけ余白を均等配分し、表図形の枠を満たす。
  const expanded =
    baseTotal < availableEm
      ? base.map((width) => width + (availableEm - baseTotal) / columnCount)
      : base;
  return expanded.map((width) => width * fontPx);
}

/**
 * セル 1 つを描くときに現れる最大の文字サイズ（px）。
 *
 * 部分書式（run）で基底より大きくした文字がある場合、その行は大きい方の文字サイズで
 * 折り返し・行送りを見積もらないと、画面より低い行高になって文字が欠ける。
 * run の `start` / `end` は**生テキスト**の添字だが、ここで使うのは最大サイズだけなので、
 * 表示テキストとの添字のずれは影響しない。
 */
function tableCellMaxFontPx(
  runs: readonly TextInlineStyleRange[] | undefined,
  fontPx: number
): number {
  if (runs === undefined) return fontPx;
  let maximum = fontPx;
  for (const run of runs) {
    const size = run.style.fontSize;
    if (typeof size === "number" && Number.isFinite(size) && size > maximum) maximum = size;
  }
  return maximum;
}

/**
 * 画面・PDF・サムネイルで共有する表図形の行高（px）を返す。
 *
 * 各行の高さは、その行のセルが列幅の中で何行に折り返すかの見積もりから決める（行ごとに異なる）。
 * 折り返した文字が切り取られないよう、**必要量が枠を超える場合は必要量をそのまま返す**
 * （枠へ押し込めない）。枠の側を広げるかどうかは呼び出し側の責務で、画面では実描画の
 * 大きさへ枠を追随させる。
 *
 * 折り返し行数は {@link estimateEmWidth} による文字幅の近似で求めるため、実際のフォントでの
 * 折り返し位置とは差が出ることがある。画面はその差を実測で補い、DOM を持たない PDF・
 * サムネイルはこの見積もりだけで描く。
 */
export function resolveTableObjectRowHeights(params: {
  /**
   * 各セルの**表示テキスト**（記法を解釈した後の、実際に画面へ出る文字列）。
   * 生テキストで測ってはいけない。`[ラベル](URL)` のように表示で縮約される記法を生のまま
   * 測ると、実際には 1 行で収まる行が何行ぶんも高く見積もられる。
   */
  readonly cellTexts: readonly (readonly string[])[];
  /** `resolveTableObjectColumnWidths` が返した列幅（px）。折り返し行数の見積もりに使う。 */
  readonly columnWidthsPx: readonly number[];
  /**
   * セルごとの部分書式。文字サイズを大きくした run がある行を、その大きさで見積もるために使う。
   * 渡さない場合は全セルが基底の `fontPx` で描かれるものとして扱う。
   */
  readonly cellInlineStyles?: TableCellInlineStyles | undefined;
  /**
   * 枠の内側の高さ（px）。必要量の合計がこれより小さいときだけ、余りを各行へ均等に配って
   * 枠を満たす。**0 を渡すと配分を行わず、配分前の必要量をそのまま返す**
   * （枠を満たす配分をブラウザの表レイアウトへ任せる画面側が使う）。
   */
  readonly availablePx: number;
  readonly fontPx: number;
}): readonly number[] {
  const rowCount = params.cellTexts.length;
  if (rowCount === 0) return [];
  const fontPx = Number.isFinite(params.fontPx) && params.fontPx > 0 ? params.fontPx : 16;
  const availablePx = Number.isFinite(params.availablePx) ? Math.max(0, params.availablePx) : 0;
  const minimumPx = TABLE_ROW_MIN_HEIGHT_EM * fontPx;

  const required = params.cellTexts.map((row, rowIndex) => {
    // 行高はセルごとの必要量の最大。セルごとに文字サイズが違いうるため、
    // 「行数の最大 × 行の最大文字サイズ」ではなく、セル単位で必要量を出してから比べる
    // （小さい文字で多く折り返すセルと、大きい文字の 1 行のセルが同居しても過大にならない）。
    let requiredPx = minimumPx;
    row.forEach((cell, columnIndex) => {
      // 部分書式で大きくした文字も欠けないよう、そのセルの最大文字サイズで見積もる。
      // 一部の範囲だけが大きい場合は保守的（高め）になるが、画面と出力の食い違いは防げる。
      const cellFontPx = tableCellMaxFontPx(
        cellInlineStylesOf(params.cellInlineStyles, rowIndex, columnIndex),
        fontPx
      );
      const columnPx = params.columnWidthsPx[columnIndex];
      // 左右の padding を除いた実際に文字が入る幅。極端に狭い列でも 1em は確保する
      // （0 以下にすると必要行数が無限大になる）。
      // 列幅を取れない（長さ不一致・不正値）場合は 1 行として扱い、例外にしない。
      const lines =
        typeof columnPx === "number" && Number.isFinite(columnPx) && columnPx > 0
          ? Math.max(
              1,
              Math.ceil(
                estimateEmWidth(cell) / Math.max(1, columnPx / cellFontPx - TABLE_CELL_PADDING_EM)
              )
            )
          : 1;
      requiredPx = Math.max(requiredPx, lines * TABLE_ROW_LINE_EM * cellFontPx);
    });
    return requiredPx;
  });

  // 枠に余りがあるときだけ、余白を各行へ均等に足して枠を満たす（列幅と同じ考え方）。
  const requiredTotal = required.reduce((sum, value) => sum + value, 0);
  if (requiredTotal >= availablePx) return required;
  const extra = (availablePx - requiredTotal) / rowCount;
  return required.map((value) => value + extra);
}

/**
 * 構造 revision の正規化。未設定（旧データ）と不正値は 0 として扱う。
 * 表の形が変わるたびに 1 つ進み、セル本文の更新では進めない。
 */
export function tableStructureRevisionOf(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) return 0;
  return value;
}

/** 構造 revision を 1 つ進める。上限に達していて進められない場合は null。 */
export function nextTableStructureRevision(value: number | undefined): number | null {
  const current = tableStructureRevisionOf(value);
  return current >= TABLE_STRUCTURE_REVISION_MAX ? null : current + 1;
}

/**
 * 作成時のクライアント指定の構造 revision を取り除く（未設定＝0 から数え直す）。
 *
 * 構造 revision はサーバだけが確定ごとに採番する値で、新しいオブジェクトは 0 から始まる。
 * 作成入力の値をそのまま保存すると、細工クライアントが上限値を指定した表を作れてしまい、
 * 以後の行・列の増減が `nextTableStructureRevision` の枯渇で恒久的に拒否される。
 * 表以外と未設定の場合はそのまま返す。
 */
export function stripTableStructureRevision(object: CanvasObject): CanvasObject {
  if (object.type !== "table" || object.structureRevision === undefined) return object;
  const { structureRevision: _ignoredRevision, ...rest } = object;
  return rest;
}

/**
 * 列幅比率を合計 1 へ正規化する。
 * 正の有限数だけを受け付け、1 要素でも条件を満たさない場合は undefined（自動列幅へ戻す）を返す。
 */
export function normalizeTableColumnFractions(
  values: readonly number[]
): readonly number[] | undefined {
  if (values.length === 0) return undefined;
  let total = 0;
  for (const value of values) {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return undefined;
    total += value;
  }
  if (!Number.isFinite(total) || total <= 0) return undefined;
  const normalized = values.map((value) => value / total);
  // 正規化の丸めで 0 へ潰れた列があると「正の比率」の不変条件を満たせないため、自動列幅へ戻す。
  return normalized.every((value) => Number.isFinite(value) && value > 0) ? normalized : undefined;
}

/** 指定位置へ 1 行を挿入する。values 未指定なら空行。範囲外・列数不一致なら null。 */
export function insertTableRow(
  cells: readonly (readonly string[])[],
  at: number,
  values?: readonly string[] | undefined
): readonly (readonly string[])[] | null {
  const columnCount = cells[0]?.length ?? 0;
  if (columnCount === 0) return null;
  if (!Number.isInteger(at) || at < 0 || at > cells.length) return null;
  if (values !== undefined && values.length !== columnCount) return null;
  const row = values !== undefined ? [...values] : new Array<string>(columnCount).fill("");
  return [...cells.slice(0, at), row, ...cells.slice(at)];
}

/** 指定位置の行を削除する。ヘッダ行（0）と範囲外は削除できない（null）。 */
export function deleteTableRow(
  cells: readonly (readonly string[])[],
  at: number
): readonly (readonly string[])[] | null {
  // ヘッダ行は表の意味そのものなので消せない（サーバ検証と同じ下限）。
  if (!Number.isInteger(at) || at < 1 || at >= cells.length) return null;
  return [...cells.slice(0, at), ...cells.slice(at + 1)];
}

/** 指定位置へ 1 列を挿入する。values 未指定なら空列。範囲外・行数不一致なら null。 */
export function insertTableColumn(
  cells: readonly (readonly string[])[],
  at: number,
  values?: readonly string[] | undefined
): readonly (readonly string[])[] | null {
  const columnCount = cells[0]?.length ?? 0;
  if (columnCount === 0) return null;
  if (!Number.isInteger(at) || at < 0 || at > columnCount) return null;
  if (values !== undefined && values.length !== cells.length) return null;
  return cells.map((row, rowIndex) => [
    ...row.slice(0, at),
    values?.[rowIndex] ?? "",
    ...row.slice(at),
  ]);
}

/** 指定位置の列を削除する。最後の 1 列と範囲外は削除できない（null）。 */
export function deleteTableColumn(
  cells: readonly (readonly string[])[],
  at: number
): readonly (readonly string[])[] | null {
  const columnCount = cells[0]?.length ?? 0;
  if (columnCount <= 1) return null;
  if (!Number.isInteger(at) || at < 0 || at >= columnCount) return null;
  return cells.map((row) => [...row.slice(0, at), ...row.slice(at + 1)]);
}

/**
 * 列の挿入にあわせて列幅比率を組み直す。
 * 既存の平均値を新しい列へ与えたうえで合計 1 へ再正規化する。
 * 自動列幅の表（比率を持たない表）では undefined のままにする。
 */
export function insertTableColumnFraction(
  fractions: readonly number[] | undefined,
  at: number,
  columnCount: number
): readonly number[] | undefined {
  if (!isValidTableColumnFractions(fractions, columnCount)) return undefined;
  // 既存の平均幅を新しい列へ与えるため、まず現在の比率の合計を求める。
  const average = fractions.reduce((sum, value) => sum + value, 0) / fractions.length;
  return normalizeTableColumnFractions([
    ...fractions.slice(0, at),
    average,
    ...fractions.slice(at),
  ]);
}

/** 列の削除にあわせて列幅比率を組み直す（残った列で合計 1 へ再正規化する）。 */
export function deleteTableColumnFraction(
  fractions: readonly number[] | undefined,
  at: number,
  columnCount: number
): readonly number[] | undefined {
  if (!isValidTableColumnFractions(fractions, columnCount)) return undefined;
  return normalizeTableColumnFractions([...fractions.slice(0, at), ...fractions.slice(at + 1)]);
}

/** セル本文を末尾から空にし、形を保ったまま指定 JSON バイト予算へ収める。 */
export function fitTableCellsForBudget(params: {
  readonly cells: readonly (readonly string[])[];
  readonly budgetBytes: number;
}): { readonly cells: readonly (readonly string[])[]; readonly clamped: boolean } | null {
  const budget = Math.max(0, Math.floor(params.budgetBytes));
  if (tableCellsJsonByteLength(params.cells) <= budget) {
    return { cells: params.cells, clamped: false };
  }
  const cells = params.cells.map((row) => [...row]);
  for (let row = cells.length - 1; row >= 0; row -= 1) {
    for (let column = cells[row]!.length - 1; column >= 0; column -= 1) {
      cells[row]![column] = "";
      if (tableCellsJsonByteLength(cells) <= budget) return { cells, clamped: true };
    }
  }
  return null;
}

/**
 * 1 セルへ入れられる本文を、表の全上限（1 セルの文字数・表全体の文字数・JSON バイト数）へ収める。
 *
 * 文字数の上限だけを通った保存不能な入力を「成功」として表示しないよう、
 * 候補の表全体へ適用した結果で判定する。切り詰めではサロゲートペアを分断しない。
 * 行・列が範囲外の場合は null を返す。
 */
export function fitTableCellText(params: {
  readonly cells: readonly (readonly string[])[];
  readonly row: number;
  readonly column: number;
  readonly text: string;
}): { readonly text: string; readonly clamped: boolean } | null {
  const row = params.cells[params.row];
  if (!row || params.column < 0 || params.column >= row.length) return null;
  let otherChars = 0;
  for (const [rowIndex, current] of params.cells.entries()) {
    for (const [columnIndex, cell] of current.entries()) {
      if (rowIndex === params.row && columnIndex === params.column) continue;
      otherChars += cell.length;
    }
  }
  const charBudget = Math.max(
    0,
    Math.min(TABLE_CELL_MAX_LENGTH, TABLE_CELLS_MAX_TOTAL_CHARS - otherChars)
  );
  let candidate = truncateWithoutSplittingSurrogate(params.text, charBudget);
  // 文字数を満たしても JSON バイト数の上限を超えることがある（1 文字が最大 6 バイトへ展開される）。
  // 収まる最長の prefix を、長さを狭めながら探す。
  while (
    candidate.length > 0 &&
    tableCellsJsonByteLength(withCellText(params.cells, params.row, params.column, candidate)) >
      TABLE_CELLS_MAX_JSON_BYTES
  ) {
    candidate = truncateWithoutSplittingSurrogate(candidate, candidate.length - 1);
  }
  return { text: candidate, clamped: candidate !== params.text };
}

/** 指定セルだけを差し替えたセル配列を返す（範囲は呼び出し側が保証する）。 */
function withCellText(
  cells: readonly (readonly string[])[],
  row: number,
  column: number,
  text: string
): readonly (readonly string[])[] {
  return cells.map((current, rowIndex) =>
    rowIndex === row
      ? current.map((cell, columnIndex) => (columnIndex === column ? text : cell))
      : current
  );
}

/** UTF-16 単位で切り詰める。末尾がサロゲートペアの前半になる場合は 1 つ手前で切る。 */
function truncateWithoutSplittingSurrogate(text: string, maxLength: number): string {
  if (maxLength <= 0) return "";
  if (text.length <= maxLength) return text;
  const code = text.charCodeAt(maxLength - 1);
  // 0xD800〜0xDBFF は上位サロゲート。ここで切ると壊れた 1 文字が残る。
  const safeLength = code >= 0xd800 && code <= 0xdbff ? maxLength - 1 : maxLength;
  return text.slice(0, Math.max(0, safeLength));
}

/**
 * 表の行・列を 1 つだけ増減する操作。
 * `cells` は削除の Undo（内容ごとの復元）で使い、省略時は空の行・列を挿入する。
 * `inlineStyles` は同じ Undo で部分書式まで復元するために使い、省略時は部分書式なしで挿入する。
 */
export type TableShapeChange =
  | {
      readonly kind: "insert-row";
      readonly at: number;
      readonly cells?: readonly string[] | undefined;
      readonly inlineStyles?: TableShapeChangeInlineStyles | undefined;
    }
  | { readonly kind: "delete-row"; readonly at: number }
  | {
      readonly kind: "insert-column";
      readonly at: number;
      readonly cells?: readonly string[] | undefined;
      readonly inlineStyles?: TableShapeChangeInlineStyles | undefined;
    }
  | { readonly kind: "delete-column"; readonly at: number };

/**
 * 挿入する行・列のセルごとの部分書式。`cells` と同じ並び（行の挿入なら列順、列の挿入なら行順）で、
 * 部分書式を持たないセルは欠落（`null`、または op を JSON 化する前のクライアント側では `undefined`）。
 */
export type TableShapeChangeInlineStyles = TableCellInlineStylesRow;

/** 表の編集を適用できなかった理由。op.reject のコードと同じ語彙にそろえる。 */
export type TableEditRejectCode = "invalid" | "conflict" | "limit";

/** 表の編集結果。失敗時は利用者向けの理由と機械可読コードを返す。 */
export type TableEditResult =
  | { readonly ok: true; readonly object: TableCanvasObject }
  | { readonly ok: false; readonly reason: string; readonly code: TableEditRejectCode };

/** 行・列の増減と入れ違ったセル更新を拒否するときの、利用者向けの理由。 */
export const TABLE_STRUCTURE_CONFLICT_REASON =
  "表の行または列が他の利用者によって変更されました。最新の表を確認して入力し直してください。";

const TABLE_CELLS_LIMIT_REASON =
  `表に入れられる文字数の上限を超えました。1 セル ${TABLE_CELL_MAX_LENGTH} 文字、` +
  `表全体で ${TABLE_CELLS_MAX_TOTAL_CHARS} 文字までです。`;

function structureMatches(table: TableCanvasObject, expected: number): boolean {
  return tableStructureRevisionOf(table.structureRevision) === expected;
}

/**
 * セル 1 つの本文を差し替える。
 *
 * row / column は「その形の表における位置」であり、行・列の増減で意味が変わる。そのため
 * 構造 revision の一致を必須とし、不一致なら適用せず conflict として返す（別のセルを
 * 書き換えてしまうのを防ぐ）。サーバ・クライアントの楽観適用で同じ関数を使い、
 * 判定と結果を一致させる。
 *
 * 部分書式（`inlineStyles`）の扱いは、テキスト図形の本文更新（`applyTextUpdateToTextObject`）と
 * 同じ契約にそろえる。
 *
 * - **配列を渡した更新**（新クライアント）: その run を権威として保存する。**空配列は
 *   「このセルの部分書式を消す」** を表す。
 * - **省略した更新**（このフィールドを知らない旧クライアント・旧タブ）: 既存の run を新しい本文へ
 *   追従（rebase）させる。**省略を「全部消す」と解釈しない。** そう解釈すると、部分書式を持つ
 *   セルを旧タブが 1 文字編集しただけで、他の利用者が付けた書式が黙って消える。
 */
export function applyTableCellUpdate(
  table: TableCanvasObject,
  input: {
    readonly row: number;
    readonly column: number;
    readonly text: string;
    readonly structureRevision: number;
    /**
     * このセルの部分書式。**省略は「旧クライアント互換の追従」**、**空配列は「明示的な削除」**を
     * 表す（`UpdateTableCellOp`）。
     */
    readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  }
): TableEditResult {
  if (!structureMatches(table, input.structureRevision)) {
    return { ok: false, reason: TABLE_STRUCTURE_CONFLICT_REASON, code: "conflict" };
  }
  const row = table.cells[input.row];
  if (!row || input.column < 0 || input.column >= row.length) {
    return { ok: false, reason: "表の範囲外のセルは編集できません。", code: "invalid" };
  }
  const nextRuns =
    input.inlineStyles === undefined
      ? rebaseCellRuns(
          cellInlineStylesAt(table, input.row, input.column),
          row[input.column] ?? "",
          input.text
        )
      : normalizeCellRuns(input.inlineStyles, input.text);
  // 本文も部分書式も変わらない再送は、op を増やすだけで意味が無い（履歴も汚す）。
  if (
    row[input.column] === input.text &&
    isSameCellInlineStyles(cellInlineStylesAt(table, input.row, input.column), nextRuns)
  ) {
    return { ok: true, object: table };
  }
  const cells = table.cells.map((current, rowIndex) =>
    rowIndex === input.row
      ? current.map((cell, columnIndex) => (columnIndex === input.column ? input.text : cell))
      : current
  );
  if (!isValidTableCells(cells)) {
    return { ok: false, reason: TABLE_CELLS_LIMIT_REASON, code: "limit" };
  }
  const next = withTableCellInlineStyles(
    table,
    cells,
    replaceCellInlineStyles(table.cellInlineStyles, cells, input.row, input.column, nextRuns)
  );
  // 正規化は重なりの解消で run を分割するため、入力が件数上限内でも結果が超えることがある。
  if (exceedsCellInlineStyleRunsLimit(next.cellInlineStyles)) {
    return { ok: false, reason: TABLE_CELL_INLINE_STYLE_RUNS_LIMIT_REASON, code: "limit" };
  }
  // 本文と部分書式の合計が上限を超える場合は、本文も保存しない（run の指す位置がずれるため）。
  if (!isValidTableStorageSize(next.cells, next.cellInlineStyles)) {
    return { ok: false, reason: TABLE_CELLS_LIMIT_REASON, code: "limit" };
  }
  return { ok: true, object: next };
}

/** 2 つのセル run 配列が同じ内容か（無変更の再送を見分けるための比較）。 */
function isSameCellInlineStyles(
  a: readonly TextInlineStyleRange[] | undefined,
  b: readonly TextInlineStyleRange[] | undefined
): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (a.length !== b.length) return false;
  return a.every((run, index) => {
    const other = b[index];
    return (
      other !== undefined &&
      run.start === other.start &&
      run.end === other.end &&
      isSameTextInlineStyle(run.style, other.style)
    );
  });
}

/**
 * 行・列を 1 つ増減する。適用に成功すると構造 revision を 1 つ進める。
 *
 * `columnFractions` は列の増減でだけ意味を持つ。行の増減で指定を受け付けると、
 * 利用者が決めた列幅が行の追加だけで失われるため、常に拒否する。
 */
export function applyTableShapeUpdate(
  table: TableCanvasObject,
  input: {
    readonly structureRevision: number;
    readonly change: TableShapeChange;
    readonly columnFractions?: readonly number[] | undefined;
  }
): TableEditResult {
  if (!structureMatches(table, input.structureRevision)) {
    return { ok: false, reason: TABLE_STRUCTURE_CONFLICT_REASON, code: "conflict" };
  }
  const nextRevision = nextTableStructureRevision(table.structureRevision);
  if (nextRevision === null) {
    return { ok: false, reason: TABLE_STRUCTURE_REVISION_EXHAUSTED_REASON, code: "limit" };
  }
  const columnCount = table.cells[0]?.length ?? 0;
  const isColumnChange =
    input.change.kind === "insert-column" || input.change.kind === "delete-column";
  const hasFractions = isValidTableColumnFractions(table.columnFractions, columnCount);
  const fractionsGuard = validateColumnFractionsInput(input.columnFractions, {
    isColumnChange,
    hasFractions,
  });
  if (fractionsGuard) return fractionsGuard;

  const cells = applyShapeChangeToCells(table.cells, input.change, columnCount);
  if (!cells.ok) return cells;
  if (!isValidTableCells(cells.value)) {
    return { ok: false, reason: TABLE_CELLS_LIMIT_REASON, code: "limit" };
  }
  // 部分書式も同じ操作で動かし、`cells` と行×列の形がずれないようにする。
  const cellInlineStyles = applyShapeChangeToInlineStyles(
    table.cellInlineStyles,
    table.cells,
    input.change
  );
  const styled = withTableCellInlineStyles(table, cells.value, cellInlineStyles);
  if (exceedsCellInlineStyleRunsLimit(styled.cellInlineStyles)) {
    return { ok: false, reason: TABLE_CELL_INLINE_STYLE_RUNS_LIMIT_REASON, code: "limit" };
  }
  if (!isValidTableStorageSize(styled.cells, styled.cellInlineStyles)) {
    return { ok: false, reason: TABLE_CELLS_LIMIT_REASON, code: "limit" };
  }
  // 列の増減では明示指定だけを採る（自動列幅の表では指定禁止＝自動のまま）。
  // 行の増減では既存の比率をそのまま保つ（未指定を「等分へ戻す」と解釈しない）。
  const columnFractions = isColumnChange
    ? input.columnFractions === undefined
      ? undefined
      : normalizeTableColumnFractions([...input.columnFractions])
    : table.columnFractions;
  const nextColumnCount = cells.value[0]?.length ?? 0;
  if (
    columnFractions !== undefined &&
    !isValidTableColumnFractions(columnFractions, nextColumnCount)
  ) {
    return { ok: false, reason: "列幅比率が列数と一致しません。", code: "invalid" };
  }
  if (isColumnChange && input.columnFractions !== undefined && columnFractions === undefined) {
    return { ok: false, reason: "列幅比率は正の有限数で指定してください。", code: "invalid" };
  }
  // 自動列幅へ戻す場合は、表示側が毎回無視する死んだ値を残さないようキーごと落とす。
  const { columnFractions: _previousFractions, ...withoutFractions } = styled;
  return {
    ok: true,
    object: {
      ...withoutFractions,
      structureRevision: nextRevision,
      ...(columnFractions === undefined ? {} : { columnFractions }),
    },
  };
}

/**
 * 行・列の増減を、セルごとの部分書式へも同じ位置で適用する。
 * 挿入では `change.inlineStyles`（削除の逆 op が載せた内容）を新しい行・列へ置き、削除では
 * その行・列を取り除く。形の最終的なそろえ方は `normalizeTableCellInlineStyles` に任せる。
 */
function applyShapeChangeToInlineStyles(
  source: TableCellInlineStyles | undefined,
  cells: readonly (readonly string[])[],
  change: TableShapeChange
): TableCellInlineStyles | undefined {
  const inserted =
    change.kind === "insert-row" || change.kind === "insert-column"
      ? change.inlineStyles
      : undefined;
  if (source === undefined && inserted === undefined) return undefined;
  // 変更前の形（`cells` の行数 × 列数）へそろえてから増減させる。行が `undefined` のまま
  // 列を挿入すると、挿入位置が先頭へずれてしまう。
  const rows = cells.map((row, rowIndex) =>
    row.map((_cell, columnIndex) => source?.[rowIndex]?.[columnIndex])
  );
  switch (change.kind) {
    case "insert-row":
      return [...rows.slice(0, change.at), inserted, ...rows.slice(change.at)];
    case "delete-row":
      return [...rows.slice(0, change.at), ...rows.slice(change.at + 1)];
    case "insert-column":
      return rows.map((row, rowIndex) => [
        ...row.slice(0, change.at),
        inserted?.[rowIndex],
        ...row.slice(change.at),
      ]);
    case "delete-column":
      return rows.map((row) => [...row.slice(0, change.at), ...row.slice(change.at + 1)]);
  }
}

/** `columnFractions` を指定してよい組み合わせかを検証する（§9.9 の契約）。 */
function validateColumnFractionsInput(
  columnFractions: readonly number[] | undefined,
  context: { readonly isColumnChange: boolean; readonly hasFractions: boolean }
): { readonly ok: false; readonly reason: string; readonly code: TableEditRejectCode } | null {
  if (!context.isColumnChange) {
    return columnFractions === undefined
      ? null
      : {
          ok: false,
          reason: "行の増減では列幅比率を指定できません。",
          code: "invalid",
        };
  }
  if (context.hasFractions && columnFractions === undefined) {
    return {
      ok: false,
      reason: "列幅を指定している表の列の増減では、新しい列幅比率が必要です。",
      code: "invalid",
    };
  }
  if (!context.hasFractions && columnFractions !== undefined) {
    return {
      ok: false,
      reason: "自動列幅の表へ列幅比率を指定することはできません。",
      code: "invalid",
    };
  }
  return null;
}

/** 行・列の増減をセル配列へ適用する。上限・下限・範囲外はここで拒否する。 */
function applyShapeChangeToCells(
  cells: readonly (readonly string[])[],
  change: TableShapeChange,
  columnCount: number
):
  | { readonly ok: true; readonly value: readonly (readonly string[])[] }
  | { readonly ok: false; readonly reason: string; readonly code: TableEditRejectCode } {
  switch (change.kind) {
    case "insert-row": {
      if (cells.length >= TABLE_MAX_ROWS) {
        return { ok: false, reason: `行は ${TABLE_MAX_ROWS} 行までです。`, code: "limit" };
      }
      const next = insertTableRow(cells, change.at, change.cells);
      return next
        ? { ok: true, value: next }
        : { ok: false, reason: "行を挿入できません。", code: "invalid" };
    }
    case "delete-row": {
      const next = deleteTableRow(cells, change.at);
      return next
        ? { ok: true, value: next }
        : { ok: false, reason: "ヘッダ行は削除できません。", code: "invalid" };
    }
    case "insert-column": {
      if (columnCount >= TABLE_MAX_COLUMNS) {
        return { ok: false, reason: `列は ${TABLE_MAX_COLUMNS} 列までです。`, code: "limit" };
      }
      const next = insertTableColumn(cells, change.at, change.cells);
      return next
        ? { ok: true, value: next }
        : { ok: false, reason: "列を挿入できません。", code: "invalid" };
    }
    case "delete-column": {
      const next = deleteTableColumn(cells, change.at);
      return next
        ? { ok: true, value: next }
        : { ok: false, reason: "最後の 1 列は削除できません。", code: "invalid" };
    }
  }
}
